import { categories, type Category } from '@/data/tools';
import * as LucideIcons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Menu } from 'lucide-react';
import { useState } from 'react';

type SidebarProps = {
  activeCategory: Category;
  onCategoryChange: (category: Category) => void;
  showExternalTools: boolean;
  onExternalToolsToggle: (show: boolean) => void;
  toolCounts?: Record<Category, number>;
  isMobile?: boolean;
  onMobileMenuOpen?: () => void;
  hideFixedMenuButton?: boolean;
};

function SidebarContent({
  activeCategory,
  onCategoryChange,
  showExternalTools,
  onExternalToolsToggle,
  toolCounts,
  onMobileClose,
}: SidebarProps & { onMobileClose?: () => void }) {
  const counts = toolCounts || ({} as Record<Category, number>);
  const handleCategoryClick = (categoryId: Category) => {
    onCategoryChange(categoryId);
    onMobileClose?.();
  };

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='p-6 pb-4'>
        <div className='flex items-center gap-3 mb-1'>
          <div className='p-2 rounded-lg bg-primary/10'>
            <LucideIcons.Wrench className='h-5 w-5 text-primary' />
          </div>
          <div>
            <h1 className='text-lg font-bold'>DevTools</h1>
            <p className='text-xs text-muted-foreground'>Toolkit</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* Categories Navigation */}
      <ScrollArea className='flex-1 h-0 px-3 py-4'>
        <div className='space-y-1'>
          <p className='px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>Categories</p>
          {categories
            .filter((cat) => cat.id !== 'favorites')
            .map((cat) => {
              const Icon = LucideIcons[cat.icon as keyof typeof LucideIcons] as any;
              const count = counts[cat.id] || 0;
              const isActive = activeCategory === cat.id;

              return (
                <TooltipProvider key={cat.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        size='sm'
                        onClick={() => handleCategoryClick(cat.id)}
                        className='w-full justify-start gap-3 h-10 px-3'
                      >
                        {Icon && <Icon className='h-4 w-4 shrink-0' />}
                        <span className='flex-1 text-left'>{cat.name}</span>
                        {count > 0 && (
                          <Badge
                            variant='secondary'
                            className='ml-auto h-5 min-w-5 px-1.5 text-xs'
                          >
                            {count}
                          </Badge>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side='right'>
                      <p>
                        {cat.name} ({count} tools)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
        </div>
      </ScrollArea>

      <Separator />

      {/* Favorites Section */}
      <div className='px-3 py-3'>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={activeCategory === 'favorites' ? 'secondary' : 'ghost'}
                size='sm'
                onClick={() => handleCategoryClick('favorites')}
                className='w-full justify-start gap-3 h-10 px-3'
              >
                <LucideIcons.Star className='h-4 w-4 shrink-0' />
                <span className='flex-1 text-left'>Favorites</span>
                {counts.favorites > 0 && (
                  <Badge
                    variant='secondary'
                    className='ml-auto h-5 min-w-5 px-1.5 text-xs'
                  >
                    {counts.favorites}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side='right'>
              <p>Favorites ({counts.favorites || 0} tools)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Separator />

      {/* Settings Section */}
      <div className='p-4 space-y-4'>
        <div className='space-y-3'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider'>Settings</p>

          {/* External Tools Toggle */}
          <div className='flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors'>
            <div className='flex items-center gap-3'>
              <LucideIcons.ExternalLink className='h-4 w-4 text-muted-foreground' />
              <div className='flex flex-col'>
                <span className='text-sm font-medium'>External Tools</span>
                <span className='text-xs text-muted-foreground'>Show external links</span>
              </div>
            </div>
            <Switch
              checked={showExternalTools}
              onCheckedChange={onExternalToolsToggle}
              size='sm'
            />
          </div>

          {/* Theme Toggle */}
          <div className='flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-accent/50 transition-colors'>
            <div className='flex items-center gap-3'>
              <LucideIcons.Palette className='h-4 w-4 text-muted-foreground' />
              <div className='flex flex-col'>
                <span className='text-sm font-medium'>Theme</span>
                <span className='text-xs text-muted-foreground'>Toggle appearance</span>
              </div>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sidebar(props: SidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMobileOpen = () => {
    setMobileOpen(true);
    props.onMobileMenuOpen?.();
  };

  // Desktop sidebar
  const desktopSidebar = (
    <aside className='hidden lg:flex w-64 h-screen border-r bg-card/50 backdrop-blur-sm'>
      <SidebarContent {...props} />
    </aside>
  );

  // Mobile sidebar
  const mobileSidebar = (
    <>
      {/* Mobile menu button - fixed position - hide when hideFixedMenuButton is true */}
      {!props.hideFixedMenuButton && (
        <div className='lg:hidden fixed top-4 left-4 z-40'>
          <Button
            variant='outline'
            size='icon'
            onClick={handleMobileOpen}
            className='h-10 w-10 bg-background shadow-lg'
          >
            <Menu className='h-5 w-5' />
          </Button>
        </div>
      )}

      {/* Mobile sheet */}
      <Sheet
        open={mobileOpen}
        onOpenChange={setMobileOpen}
      >
        <SheetContent
          side='left'
          className='p-0 w-64'
        >
          <SidebarContent
            {...props}
            onMobileClose={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );

  return (
    <>
      {desktopSidebar}
      {mobileSidebar}
    </>
  );
}

// Export mobile menu trigger as a separate component
export function MobileMenuTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant='outline'
      size='icon'
      onClick={onClick}
      className='h-10 w-10 bg-background shadow-lg lg:hidden'
    >
      <Menu className='h-5 w-5' />
    </Button>
  );
}
